import React, { useState } from "react";
import { Bell, Loader2, X, CheckCircle2 } from "lucide-react";
import { useApp } from "../AppContext";
import api from "../api";
import { toast } from "sonner";

const NotifyMeModal = ({ item, onClose }) => {
    const { t, lang, apiBase } = useApp();
    const [form, setForm] = useState({ phone: "", email: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!item) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.phone.trim()) {
            toast.error(
                lang === "es" ? "Por favor agrega tu teléfono" : "Please add your phone"
            );
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`${apiBase}/notify-subscribe`, {
                phone: form.phone,
                email: form.email || null,
                item_slug: item.slug,
            });
            setDone(true);
            toast.success(
                lang === "es"
                    ? "¡Listo! Te avisaremos en cuanto esté disponible."
                    : "Done! We'll let you know when it's available."
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="notify-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-earth-900/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            >
                <div className="absolute top-0 left-0 right-0 h-1 flex">
                    <div className="flex-1" style={{ background: "#002D62" }} />
                    <div className="flex-1 bg-white" />
                    <div className="flex-1" style={{ background: "#CE1126" }} />
                </div>
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-earth-100 z-10"
                    data-testid="notify-close-btn"
                >
                    <X className="w-5 h-5" />
                </button>

                {done ? (
                    <div className="p-10 text-center">
                        <div className="w-16 h-16 rounded-full bg-leaf-500 text-white flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h3 className="font-serif font-black text-2xl text-earth-900">
                            {lang === "es" ? "¡Estás en la lista!" : "You're on the list!"}
                        </h3>
                        <p className="text-sm text-earth-600 mt-3">
                            {lang === "es"
                                ? `Te avisaremos cuando ${item.name_es} esté disponible.`
                                : `We'll let you know when ${item.name_en} is available.`}
                        </p>
                        <button
                            onClick={onClose}
                            data-testid="notify-done-btn"
                            className="btn-primary mt-6"
                        >
                            {t.common.close}
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-8" data-testid="notify-form">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-11 h-11 rounded-full bg-terracotta-500 text-white flex items-center justify-center">
                                <Bell className="w-5 h-5" />
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600">
                                {lang === "es" ? "Lista de espera" : "Waiting list"}
                            </div>
                        </div>
                        <h3 className="font-serif font-black text-2xl text-earth-900 leading-tight">
                            {lang === "es" ? "Avísame cuando llegue" : "Notify me when ready"}
                        </h3>
                        <p className="text-sm text-earth-700 mt-2 mb-6">
                            {lang === "es"
                                ? `Te avisamos en cuanto ${item.name_es} esté disponible para ordenar.`
                                : `We'll notify you the moment ${item.name_en} is ready to order.`}
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                    {lang === "es" ? "Teléfono" : "Phone"}{" "}
                                    <span className="text-terracotta-500">*</span>
                                </label>
                                <input
                                    data-testid="notify-phone-input"
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm({ ...form, phone: e.target.value })
                                    }
                                    placeholder="+1 876 ..."
                                    required
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                                    Email {lang === "es" ? "(opcional)" : "(optional)"}
                                </label>
                                <input
                                    data-testid="notify-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({ ...form, email: e.target.value })
                                    }
                                    placeholder="tu@email.com"
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="notify-submit-btn"
                            disabled={submitting}
                            className="btn-primary w-full mt-6"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Bell className="w-4 h-4" />
                            )}
                            {submitting
                                ? lang === "es" ? "Inscribiendo..." : "Joining..."
                                : lang === "es" ? "Avísame" : "Notify me"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default NotifyMeModal;
